<template>
  <div class="bg-background min-h-screen">
    <!-- Hero Section -->
    <section class="bg-card border-b border-border py-16">
      <div class="container mx-auto px-4">
        <h1 class="text-4xl font-bold mb-4 text-foreground">Privacy Policy</h1>
        <p class="text-xl text-muted-foreground">Last updated: October 16, 2026</p>
      </div>
    </section>

    <!-- Content Section -->
    <section class="container mx-auto px-4 py-12">
      <div class="max-w-4xl mx-auto bg-card border border-border rounded-lg shadow-md p-8">
        <div class="prose max-w-none">
          <h2 class="text-2xl font-bold mb-4">Introduction</h2>
          <p class="mb-6">
            At Applypa, operated by Elevate Solution Experts ("Applypa," "we," "us," or "our"), we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. By using Applypa, you consent to the practices described in this policy.
          </p>

          <h2 class="text-2xl font-bold mb-4">Information We Collect</h2>
          <h3 class="text-xl font-semibold mb-3">Personal Information</h3>
          <p class="mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
          <ul class="list-disc pl-6 mb-6">
            <li>Create an account or sign in (including via Google)</li>
            <li>Build your job seeker profile, including work experience, education, and skills</li>
            <li>Upload or generate resumes and cover letters</li>
            <li>Apply for jobs or send application follow-ups</li>
            <li>Subscribe to a paid plan or make a payment</li>
            <li>Contact us or request support</li>
            <li>Subscribe to job alerts or newsletters</li>
            <li>Connect a third-party messaging account (such as Telegram or Discord) to receive job alerts</li>
          </ul>

          <h3 class="text-xl font-semibold mb-3">Usage Information</h3>
          <p class="mb-4">We automatically collect certain information about your device and how you interact with our website, including:</p>
          <ul class="list-disc pl-6 mb-6">
            <li>Device information</li>
            <li>IP address</li>
            <li>Browser type</li>
            <li>Pages visited</li>
            <li>Time spent on pages</li>
          </ul>

          <h3 class="text-xl font-semibold mb-3">Cookies and Similar Technologies</h3>
          <p class="mb-6">
            We use cookies and similar technologies to keep you signed in, remember your preferences, and understand how the Service is used. You can control cookies through your browser settings, though disabling them may affect certain features.
          </p>

          <h2 class="text-2xl font-bold mb-4">How We Use Your Information</h2>
          <p class="mb-4">We use the information we collect to:</p>
          <ul class="list-disc pl-6 mb-6">
            <li>Provide, operate, and maintain our services</li>
            <li>Match you with relevant job opportunities and potential employers</li>
            <li>Power AI features such as resume parsing, cover letter generation, and application follow-ups</li>
            <li>Send you job alerts, newsletters, and service-related notifications</li>
            <li>Process payments and manage your subscription</li>
            <li>Improve our website, features, and overall user experience</li>
            <li>Communicate with you and respond to support requests</li>
            <li>Detect, prevent, and address fraud, abuse, and security issues</li>
          </ul>

          <h2 class="text-2xl font-bold mb-4">AI Processing</h2>
          <p class="mb-6">
            Some features rely on third-party artificial intelligence providers to process your content (for example, parsing an uploaded resume or generating a cover letter). When you use these features, the relevant content may be sent to our AI provider solely to deliver the requested result. We do not sell this content, and we work with providers that handle data under appropriate confidentiality and security commitments.
          </p>

          <h2 class="text-2xl font-bold mb-4">Free Tools</h2>
          <p class="mb-6">
            Our free tools, such as the Upwork Profile Optimizer, can be used without an account. When you use one, we record what you submit (for example, your profile title and overview), the result we return, the time of the request, your IP address, and your account if you are signed in. We use this to apply fair daily usage limits, prevent abuse, and improve the tools. Your submission is processed by our AI provider as described above. We automatically delete these records after 90 days.
          </p>

          <h2 class="text-2xl font-bold mb-4">Messaging Platform Integrations (Telegram, Discord)</h2>
          <p class="mb-4">
            If you choose to connect a third-party messaging account to receive job alerts, we collect and store limited information needed to deliver those alerts, including:
          </p>
          <ul class="list-disc pl-6 mb-6">
            <li>Your platform-assigned chat or user identifier (for example, a Telegram chat ID or Discord user ID)</li>
            <li>The messaging channel used to deliver alerts to you</li>
            <li>Your public username or display name on that platform, where provided by the platform</li>
            <li>The date you connected the integration and the timestamp of the last alert sent</li>
          </ul>
          <p class="mb-6">
            This information is used solely to send you the job alerts and account notifications you've opted into, and is shared only with the messaging platform itself (Telegram or Discord) as necessary to deliver those messages. We do not use this information for advertising, and we do not access your messages, contacts, or activity on the connected platform beyond what the platform provides through its bot/application interface. You can disconnect a messaging integration at any time from your account settings, which stops further alerts and deletes the stored connection.
          </p>

          <h2 class="text-2xl font-bold mb-4">Applypa Chrome Extension</h2>
          <p class="mb-4">
            The Applypa Chrome extension lets you use your Applypa cover letters and resume when applying on OnlineJobs.ph, JobStreet and Upwork. It runs only on onlinejobs.ph, jobstreet.com, jobstreet.com.ph, upwork.com and applypa.com. It does not run on any other website and does not collect your browsing history.
          </p>
          <p class="mb-4">To work, the extension handles the following information:</p>
          <ul class="list-disc pl-6 mb-4">
            <li><strong>Your Applypa sign-in token and account email.</strong> When you are signed in at applypa.com, the extension picks up your sign-in token so it can connect to your Applypa account, and loads your account email to show which account is connected.</li>
            <li><strong>The job you are viewing.</strong> On an OnlineJobs.ph, JobStreet or Upwork job page, the extension reads the job's ID and title from the page and sends the job ID to Applypa to find, generate or rewrite your cover letter for that job.</li>
            <li><strong>Your cover letter and resume.</strong> The extension loads your cover letter from Applypa, and on JobStreet your resume file, so you can review them and add them to the application form. Nothing is entered into the form until you click "Insert into form", and the extension never submits an application for you.</li>
            <li><strong>Your edits.</strong> Changes you make to a cover letter in the extension are saved on your device so they are not lost if the page reloads.</li>
          </ul>
          <p class="mb-6">
            The sign-in token, account email and your edits are stored only in the extension's local storage on your device. Information sent to Applypa is handled as described in the rest of this policy. Generating or rewriting a cover letter uses the AI processing described above and counts against your plan's AI credits. The extension does not sell your data, use it for advertising, or share it with anyone other than Applypa. You can remove the stored sign-in at any time by clicking "Disconnect" in the extension, signing out of Applypa, or uninstalling the extension.
          </p>

          <h2 class="text-2xl font-bold mb-4">International Data Processing</h2>
          <p class="mb-6">
            Applypa relies on trusted third-party service providers, including cloud infrastructure, payment, and AI providers. As a result, your information may be processed or stored in countries outside the Philippines. Wherever your information is processed, we take reasonable steps to ensure it is handled securely and in accordance with this Privacy Policy.
          </p>

          <h2 class="text-2xl font-bold mb-4">Information Sharing</h2>
          <p class="mb-4">We may share your information in the following circumstances:</p>
          <ul class="list-disc pl-6 mb-6">
            <li><strong>With employers</strong> when you apply for jobs or share your profile through our platform</li>
            <li><strong>With service providers</strong> who help us operate the Service, such as our payment processor (PayMongo), hosting providers, email/notification services, messaging platforms (such as Telegram or Discord, if you connect them), and AI providers</li>
            <li><strong>For legal reasons</strong>, when required to comply with applicable law, regulation, legal process, or enforceable government request</li>
            <li><strong>To protect rights and safety</strong>, including enforcing our terms and protecting Applypa, our users, or the public</li>
          </ul>
          <p class="mb-6">
            We do <strong>not</strong> sell your personal information to third parties.
          </p>

          <h2 class="text-2xl font-bold mb-4">Data Security</h2>
          <p class="mb-6">
            We implement reasonable technical and organizational measures to protect your information, including encryption of sensitive data. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
          </p>

          <h2 class="text-2xl font-bold mb-4">Data Retention</h2>
          <p class="mb-6">
            We retain your personal information for as long as your account is active or as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements. When information is no longer required, we will delete or anonymize it.
          </p>

          <h2 class="text-2xl font-bold mb-4">Your Rights</h2>
          <p class="mb-4">Subject to applicable law, including the Philippine Data Privacy Act of 2012, you have the right to:</p>
          <ul class="list-disc pl-6 mb-6">
            <li>Access the personal information we hold about you</li>
            <li>Correct inaccurate or incomplete information</li>
            <li>Request deletion of your information</li>
            <li>Object to or restrict certain processing</li>
            <li>Opt out of marketing communications at any time</li>
          </ul>
          <p class="mb-6">
            To exercise any of these rights, please contact us using the details below.
          </p>

          <h2 class="text-2xl font-bold mb-4">Children's Privacy</h2>
          <p class="mb-6">
            The Service is not intended for individuals under 18 years of age, and we do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can remove it.
          </p>

          <h2 class="text-2xl font-bold mb-4">Changes to This Policy</h2>
          <p class="mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date above.
          </p>

          <h2 class="text-2xl font-bold mb-4">Contact Us</h2>
          <p class="mb-6">
            If you have any questions about this Privacy Policy or how we handle your information, please contact us at:
            <br />
            <a href="mailto:support@applypa.com" class="text-primary hover:underline">support@applypa.com</a>
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicyPage'
}
</script>
